import React, { forwardRef, useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Section from '@polestar/component-warehouse-react/components/section/Section';
import MediaQueries, { createMediaQuery, } from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import Caption from '@polestar/component-warehouse-react/components/caption/Caption';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import Emphasis from '@polestar/component-warehouse-react/tokens/emphasis/Emphasis';
import Divider from '@polestar/component-warehouse-react/components/divider/Divider';
import FooterDoormat from './internal/FooterDoormat';
import Link, { LinkVariants, } from '@polestar/component-warehouse-react/components/link/Link';
import docgenInterface from '@polestar/component-warehouse-react/helpers/docgenInterface';
import { minimalHitbox } from '@polestar/component-warehouse-react/helpers/style';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { createCSSTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { createCustomFontSize } from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import { createCustomLineHeight } from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import { createCustomLetterSpacing } from '@polestar/component-warehouse-react/tokens/letter-spacings/LetterSpacings';
import { MainAxisAlignments, CrossAxisAlignments, } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Grids from '@polestar/component-warehouse-react/tokens/grids/Grids';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
const Footer = forwardRef(({ copyrightText = `Polestar © ${new Date().getFullYear()} All rights reserved`, disclaimerText, marketSelector, listGroups, legalLinks, icpLink, banner, ...props }, ref) => {
    var _a, _b;
    const isMobile = ((_a = useMediaQuery()) === null || _a === void 0 ? void 0 : _a.name) === 'mobile';
    const marketSelectorComponent = useMemo(() => {
        // due to many re-renders because of useMediaQuery it is necessary to keep this only render when marketSelector does not change, while it controls also the opened Drawer instance which should not be cleared with a re-render.
        return (React.createElement(MarketSelectorWrapper, { key: "market-selector-wrapper" }, typeof marketSelector === 'function' ? marketSelector() : marketSelector));
    }, [marketSelector]);
    return (React.createElement(Wrapper, { ...props, as: "footer", ref: ref },
        (listGroups || banner) && (React.createElement(React.Fragment, null,
            React.createElement(FooterDoormat, { listGroups: listGroups || [], content: banner }),
            React.createElement(Spacer, { spacing: {
                    mobile: Spacings.small,
                    tablet: Spacings.large,
                    desktop: Spacings.large,
                } }))),
        React.createElement(Container, null,
            !!marketSelector && isMobile && marketSelectorComponent,
            !!copyrightText && (React.createElement(CopyrightText, null,
                React.createElement(Caption, { size: Sizes.small }, copyrightText),
                !!icpLink && (React.createElement(IcpWrapper, null,
                    React.createElement(LegalLink, { variant: LinkVariants.legal, hideUnderline: true, ...icpLink }, icpLink.label))))),
            !!legalLinks && (React.createElement(LegalLinksWrapper, { hasLinks: ((_b = legalLinks.items) === null || _b === void 0 ? void 0 : _b.length) !== 0 }, legalLinks.items.map(({ label, href, as, ...props }, index) => (React.createElement(LegalLink, { variant: LinkVariants.legal, href: href, key: index, as: as, hideUnderline: true, ...props }, label))))),
            !!marketSelector && !isMobile && marketSelectorComponent),
        !!disclaimerText && (React.createElement(React.Fragment, null,
            React.createElement(Divider, { mainAxisAlignment: MainAxisAlignments.end, emphasis: Emphasis.low, spacing: Spacings.large }),
            React.createElement(Spacer, { spacing: Spacings.large }),
            React.createElement(DisclaimerText, null,
                React.createElement(Caption, { size: Sizes.small }, disclaimerText))))));
});
Footer.displayName = 'Footer';
export default Footer;
/**
 * @internal
 */
export const ListGroupItemPropsInterface = docgenInterface();
const commonWrapperStyles = css `
  font-size: ${createCustomFontSize(0.75)};
  line-height: ${createCustomLineHeight(0.875)};
  letter-spacing: ${createCustomLetterSpacing(-0.00625)};
`;
const Wrapper = styled(Section)(({ theme }) => css `
    background-color: ${theme.footer.backgroundColor};
    transition: ${createCSSTransition('background-color')};

    ${responsiveStyles({
    paddingBlockStart: theme.footer.padding,
    paddingBlockEnd: theme.footer.padding,
})}
  `);
const Container = styled.div `
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(3, max-content);
  overflow: hidden;

  ${MediaQueries.desktop} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: repeat(2, auto);
  }

  ${MediaQueries.desktopWide} {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;
const CopyrightText = styled.div `
  grid-row: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${createMediaQuery(460)} {
    grid-row: 2;
    flex-direction: row;
    justify-content: flex-start;
    padding-block-start: ${Spacings.large};
  }

  ${MediaQueries.tablet} {
    grid-row: 1;
    grid-column: 1;
    padding-block-start: unset;
  }
`;
const DisclaimerText = styled.div `
  position: relative;

  ${MediaQueries.desktop} {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: ${Grids.desktop.gutter};

    & > div {
      grid-column: 1 / span 8;
    }
  }
`;
const IcpWrapper = styled.div(({ theme }) => css `
    ${commonWrapperStyles};
    display: flex;
    margin-block-start: ${Spacings.xSmall};

    ${createMediaQuery(460)} {
      margin-block-start: unset;

      &::before {
        content: '|';
        margin-inline: ${Spacings.medium};
        color: ${theme.footer.legal.pipeColor};
        justify-self: center;
      }
    }

    a {
      white-space: nowrap;
    }
  `);
const LegalLinksWrapper = styled.div(({ theme, hasLinks }) => css(css `
      ${commonWrapperStyles};
      width: 100%;
      display: flex;
      flex-flow: wrap;
      grid-row: 3;
      gap: ${Spacings.medium};
      padding-block-start: ${Spacings.large};

      ${MediaQueries.tablet} {
        grid-column: 1/3;
        grid-row: 2;
      }

      ${MediaQueries.desktop} {
        border: unset;
        margin-block-start: unset;
        width: unset;
        padding-block-start: ${Spacings.xSmall};
      }

      ${MediaQueries.desktopWide} {
        padding-block-start: unset;
      }
    `, hasLinks &&
    css `
        ${MediaQueries.desktopWide} {
          &::before {
            content: '|';
            margin-inline-start: ${Spacings.medium};
            color: ${theme.footer.legal.pipeColor};
            justify-self: center;
          }
        }
      `));
const LegalLink = styled(Link) `
  position: relative;

  @media (hover: none) {
    ${minimalHitbox}
  }
`;
const MarketSelectorWrapper = styled.div `
  display: inline-flex;
  flex-shrink: 0;
  margin-inline: 0 auto;
  grid-row: 1;
  grid-column: 1;
  padding-block-end: ${Spacings.large};
  align-items: ${CrossAxisAlignments.center};

  ${createMediaQuery(460)} {
    padding-block-end: unset;
  }

  ${MediaQueries.tablet} {
    grid-row: 1;
    grid-column: 2;
  }

  ${MediaQueries.desktop} {
    align-items: ${CrossAxisAlignments.start};
    margin-inline: auto 0;
    grid-column: 2;
    grid-row: 1;
  }
`;
